import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BudgetCreationForm = ({ activeBudget, onBudgetCreate }) => {
  const [formData, setFormData] = useState({
    name: activeBudget?.name || '',
    duration: activeBudget?.duration || 'monthly',
    totalAmount: activeBudget?.totalAmount || 0
  });
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  const durationOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annual', label: 'Annual' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAmountChange = (e) => {
    const value = e?.target?.value?.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, totalAmount: value ? parseInt(value) : 0 }));
    if (errors?.totalAmount) {
      setErrors(prev => ({ ...prev, totalAmount: '' }));
    }
  };

  const handleDurationChange = (value) => {
    setFormData(prev => ({ ...prev, duration: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Budget name is required';
    }

    if (!formData?.totalAmount || formData?.totalAmount <= 0) {
      newErrors.totalAmount = 'Total amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    const budgetData = {
      ...formData,
      totalAmount: parseInt(formData?.totalAmount),
      startDate: new Date()?.toISOString()?.split('T')?.[0],
      endDate: calculateEndDate(formData?.duration)
    };

    onBudgetCreate(budgetData);
    setIsEditing(false);
  };

  const calculateEndDate = (duration) => {
    const today = new Date();
    let endDate = new Date(today);

    switch (duration) {
      case 'weekly':
        endDate?.setDate(today?.getDate() + 7);
        break;
      case 'monthly':
        endDate?.setMonth(today?.getMonth() + 1);
        break;
      case 'quarterly':
        endDate?.setMonth(today?.getMonth() + 3);
        break;
      case 'annual':
        endDate?.setFullYear(today?.getFullYear() + 1);
        break;
      default:
        endDate?.setMonth(today?.getMonth() + 1);
    }

    return endDate?.toISOString()?.split('T')?.[0];
  };

  const handleReset = () => {
    setFormData({
      name: activeBudget?.name || '',
      duration: activeBudget?.duration || 'monthly',
      totalAmount: activeBudget?.totalAmount || 0
    });
    setErrors({});
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="FileText" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Budget Creation</h2>
            <p className="text-sm text-muted-foreground">Configure your budget parameters</p>
          </div>
        </div>
        {!isEditing && (
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Edit" 
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Budget Name"
          type="text"
          name="name"
          placeholder="e.g., Monthly Budget - February 2026"
          value={formData?.name}
          onChange={handleInputChange}
          error={errors?.name}
          required
          disabled={!isEditing}
        />

        <Select
          label="Duration"
          options={durationOptions}
          value={formData?.duration}
          onChange={handleDurationChange}
          placeholder="Select duration"
          required
          disabled={!isEditing}
        />

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Total Amount <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">$</span>
            <input
              type="text"
              name="totalAmount"
              value={formData?.totalAmount?.toLocaleString()}
              onChange={handleAmountChange}
              placeholder="0"
              disabled={!isEditing}
              className={`flex h-10 w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors?.totalAmount ? 'border-destructive' : ''}`}
            />
          </div>
          {errors?.totalAmount && (
            <p className="text-sm text-destructive mt-1">{errors?.totalAmount}</p>
          )}
        </div>

        {isEditing && (
          <div className="flex gap-3 pt-2">
            <Button type="submit" variant="default" fullWidth iconName="Save" iconPosition="left">
              Save Budget
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleReset}>
              Cancel
            </Button>
          </div>
        )}
      </form>

      {!isEditing && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Start Date</p>
              <p className="text-sm font-medium text-foreground">{activeBudget?.startDate}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">End Date</p>
              <p className="text-sm font-medium text-foreground">{activeBudget?.endDate}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetCreationForm;